/**
 * TicketPreviewModal.tsx
 * 
 * Vista previa obligatoria del ticket antes/después de imprimir.
 * Renderiza el ticket con el mismo payload que se usaría en impresión.
 * Respeta ancho real (80mm / 58mm).
 * Incluye opción de enviar por WhatsApp como imagen JPG.
 * 
 * Props:
 *  - payload: TicketPayload (null = cerrado)
 *  - onClose: cerrar modal
 */
import React, { useRef, useEffect, useState } from 'react';
import { Modal, Button } from '../../../components/ui';
import { Printer, X, Send, Bluetooth } from 'lucide-react';
import { TicketPayload, renderTicketPreviewHTML, imprimirTicket } from '../../../services/ticketService';
import {
  connectBluetoothPrinter,
  payloadToTicketText,
  printTicketBluetooth,
  getSavedBluetoothPrinter,
} from '../../../utils/bluetoothPrinter';
import html2canvas from 'html2canvas';

interface TicketPreviewModalProps {
  payload: TicketPayload | null;
  onClose: () => void;
  autoShow?: boolean;
}

export const TicketPreviewModal: React.FC<TicketPreviewModalProps> = ({
  payload,
  onClose,
  autoShow = true,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [printed, setPrinted] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [shareStatus, setShareStatus] = useState<string | null>(null);
  const [bluetoothConectando, setBluetoothConectando] = useState(false);
  const [bluetoothImprimiendo, setBluetoothImprimiendo] = useState(false);
  const [bluetoothEstado, setBluetoothEstado] = useState<string | null>(null);

  const isOpen = !!payload && autoShow;

  // Escribir HTML del ticket en el iframe cuando cambie el payload
  useEffect(() => {
    if (!payload || !iframeRef.current) return;

    const html = renderTicketPreviewHTML(payload);
    const iframe = iframeRef.current;
    
    // Usar srcdoc para evitar problemas cross-origin
    iframe.srcdoc = html;
    
    setPrinted(false);
    setPrintError(null);
    setShareStatus(null);
    setBluetoothEstado(getSavedBluetoothPrinter() ? 'Impresora BT detectada' : null);
  }, [payload]);

  const handlePrint = async () => {
    if (!payload || printing) return;

    setPrinting(true);

    try {
      const ok = imprimirTicket(payload);
      if (ok) {
        setPrinted(true);
        setPrintError(null);
        console.log('[TicketPreview] Impresión lanzada para:', payload.ticketCode);
        onClose();
      } else {
        setPrintError('No se pudo abrir la ventana de impresión. Verifica que no esté bloqueada por el navegador.');
      }
    } catch (err: any) {
      setPrintError(err.message || 'Error al imprimir');
      console.error('[TicketPreview] Error:', err);
    } finally {
      setPrinting(false);
    }
  };

  const handleWhatsApp = async () => {
    if (!payload) return;

    setSharing(true);
    setShareStatus('Generando imagen...');
    setPrintError(null);

    try {
      const anchoMM = payload.config.ancho;
      const anchoPx = anchoMM === 58 ? 384 : 512;

      // Obtener HTML del ticket
      const html = renderTicketPreviewHTML(payload);

      // Extraer estilos y contenido del body
      const styleMatch = html.match(/<style>([\s\S]*?)<\/style>/);
      const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/);

      if (!bodyMatch) {
        throw new Error('No se pudo extraer el contenido del ticket');
      }

      // Crear contenedor temporal off-screen
      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.style.zIndex = '-1';
      container.style.opacity = '1';

      // Wrapper con estilos del body del ticket
      const wrapper = document.createElement('div');
      wrapper.style.width = `${anchoPx}px`;
      wrapper.style.maxWidth = `${anchoPx}px`;
      wrapper.style.fontFamily = "'Courier New', Courier, monospace";
      wrapper.style.lineHeight = '1.35';
      wrapper.style.color = '#000';
      wrapper.style.background = '#fff';
      wrapper.style.padding = '6px 4px 10px';
      wrapper.style.textAlign = 'center';
      wrapper.style.boxSizing = 'border-box';

      // Inyectar estilos
      if (styleMatch) {
        const styleEl = document.createElement('style');
        styleEl.textContent = styleMatch[1];
        container.appendChild(styleEl);
      }

      // Inyectar body content (sin scripts)
      const bodyContent = bodyMatch[1].replace(/<script[\s\S]*?<\/script>/gi, '');
      wrapper.innerHTML = bodyContent;

      // Generar barcode como canvas en lugar del SVG (que necesitaba JsBarcode script)
      if (!payload.esReimpresion) {
        const barcodeSvg = wrapper.querySelector('#ticket-barcode');
        const barcodeWrap = wrapper.querySelector('.barcode-wrap');
        if (barcodeWrap) {
          // Crear barcode usando canvas de forma manual
          try {
            const barcodeCanvas = document.createElement('canvas');
            barcodeCanvas.width = anchoPx - 16;
            barcodeCanvas.height = 50;
            const ctx = barcodeCanvas.getContext('2d');
            if (ctx) {
              // Texto del código de barras como fallback visual
              ctx.fillStyle = '#ffffff';
              ctx.fillRect(0, 0, barcodeCanvas.width, barcodeCanvas.height);
              ctx.fillStyle = '#000000';
              ctx.font = 'bold 14px Courier New';
              ctx.textAlign = 'center';
              ctx.fillText(payload.ticketCode, barcodeCanvas.width / 2, 30);
            }
            if (barcodeSvg) barcodeSvg.remove();
            barcodeWrap.innerHTML = '';
            barcodeWrap.appendChild(barcodeCanvas);
          } catch {
            // Si falla el barcode, simplemente lo omitimos
            if (barcodeSvg) barcodeSvg.remove();
          }
        }
      }

      container.appendChild(wrapper);
      document.body.appendChild(container);

      // Esperar un tick para que el DOM se renderice
      await new Promise(r => setTimeout(r, 100));

      // Capturar con html2canvas
      const canvas = await html2canvas(wrapper, {
        backgroundColor: '#ffffff',
        scale: 2,
        width: anchoPx,
        useCORS: true,
        logging: false,
      });

      // Convertir a blob JPG
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          b => b ? resolve(b) : reject(new Error('Error al generar imagen')),
          'image/jpeg',
          0.92
        );
      });

      // Limpiar contenedor temporal
      document.body.removeChild(container);

      const fileName = `ticket-${payload.ticketCode}.jpg`;
      const file = new File([blob], fileName, { type: 'image/jpeg' });

      // Compartir con Web Share API (adjunta la imagen automáticamente)
      if (navigator.share && navigator.canShare) {
        const shareData = { files: [file], title: `Ticket ${payload.ticketCode}` };
        if (navigator.canShare(shareData)) {
          setShareStatus('Selecciona WhatsApp para enviar...');
          await navigator.share(shareData);
          setShareStatus('✓ Compartido exitosamente');
          return;
        }
      }

      // Fallback: copiar al portapapeles + abrir WhatsApp
      try {
        const pngBlob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            b => b ? resolve(b) : reject(new Error('Error PNG')),
            'image/png'
          );
        });
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': pngBlob })
        ]);
        setShareStatus('✓ Imagen copiada. Pégala en WhatsApp con Ctrl+V');
      } catch {
        // Si falla clipboard, descargar archivo
        const jpgUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = jpgUrl;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(jpgUrl);
        setShareStatus('✓ Imagen descargada. Adjúntala en WhatsApp');
      }

      const msg = `🎟 *Ticket: ${payload.ticketCode}*\n💰 Total: ₡${payload.total.toLocaleString()}\n📋 ${payload.sorteoNombre}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');

    } catch (err: any) {
      console.error('[TicketPreview] Error al compartir:', err);
      if (err.name !== 'AbortError') {
        setPrintError(err.message || 'Error al compartir por WhatsApp');
      }
      setShareStatus(null);
    } finally {
      setSharing(false);
    }
  };

  const handleConectarBluetooth = async () => {
    setBluetoothConectando(true);
    setPrintError(null);
    try {
      const printer = await connectBluetoothPrinter();
      setBluetoothEstado(`Conectada: ${printer.name}`);
    } catch (err: any) {
      console.error('[TicketPreview] Error conectando Bluetooth:', err);
      setPrintError(err?.message || 'No se pudo conectar la impresora Bluetooth.');
    } finally {
      setBluetoothConectando(false);
    }
  };

  const handlePrintBluetooth = async () => {
    if (!payload) {
      return;
    }

    setBluetoothImprimiendo(true);
    setPrintError(null);

    try {
      const ticketText = payloadToTicketText({
        ticketCode: payload.ticketCode,
        sorteoNombre: payload.sorteoNombre,
        fecha: payload.fecha,
        total: payload.total,
        jugadas: payload.jugadas,
      });

      await printTicketBluetooth(ticketText, { ancho: payload.config.ancho });
      setBluetoothEstado('✓ Ticket enviado por Bluetooth');
      setPrinted(true);
    } catch (err: any) {
      console.error('[TicketPreview] Error imprimiendo Bluetooth:', err);
      setPrintError(err?.message || 'No se pudo imprimir por Bluetooth.');
    } finally {
      setBluetoothImprimiendo(false);
    }
  };

  // Atajo de teclado: Enter = imprimir, ESC = cerrar
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === '*') {
        e.preventDefault();
        e.stopPropagation();
        handlePrint();
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, payload]);

  if (!payload) return null;

  const anchoMM = payload.config.ancho;
  const anchoPx = anchoMM === 58 ? 384 : 512;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" size="lg">
      <div className="flex flex-col items-center gap-4">
        {/* Reimpresión banner */}
        {payload.esReimpresion && (
          <div className="w-full bg-red-50 border-2 border-dashed border-red-400 text-red-700 text-center py-2 px-4 rounded-lg font-bold text-sm tracking-wider">
            *** REIMPRESIÓN ***
          </div>
        )}

        {/* Label */}
        <div className="text-center">
          <h3 className="text-lg font-bold text-gray-900">
            {payload.esReimpresion ? 'Reimpresión de ticket' : 'Vista previa del ticket'}
          </h3>
          <p className="text-sm text-gray-500">
            Ticket: <span className="font-mono font-bold">{payload.ticketCode}</span>
            {' · '}Ancho: {anchoMM}mm
          </p>
        </div>

        {/* Ticket preview iframe */}
        <div 
          className="border-2 border-gray-300 rounded-lg shadow-inner bg-white overflow-hidden"
          style={{ width: `${Math.min(anchoPx + 16, 528)}px` }}
        >
          <iframe
            ref={iframeRef}
            title="Ticket Preview"
            style={{
              width: `${anchoPx}px`,
              minHeight: '400px',
              maxHeight: '600px',
              border: 'none',
              margin: '8px auto',
              display: 'block',
            }}
            sandbox="allow-scripts"
          />
        </div>

        {/* Error */}
        {printError && (
          <div className="w-full bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
            {printError}
          </div>
        )}

        {/* Printed confirmation */}
        {printed && (
          <div className="w-full bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-lg text-sm text-center">
            ✓ Enviado a impresión
          </div>
        )}

        {/* Share status */}
        {shareStatus && (
          <div className="w-full bg-blue-50 border border-blue-200 text-blue-700 px-4 py-2 rounded-lg text-sm text-center">
            {shareStatus}
          </div>
        )}

        {/* Bluetooth status */}
        {bluetoothEstado && (
          <div className="w-full bg-indigo-50 border border-indigo-200 text-indigo-700 px-4 py-2 rounded-lg text-sm text-center">
            {bluetoothEstado}
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full max-w-2xl">
          <Button
            variant="secondary"
            onClick={onClose}
            className="flex items-center justify-center gap-2"
          >
            <X size={16} />
            Cerrar
          </Button>
          <Button
            variant="secondary"
            onClick={handleWhatsApp}
            disabled={sharing}
            className="flex items-center justify-center gap-2 bg-green-100 hover:bg-green-200 text-green-800 border-green-300"
          >
            <Send size={16} />
            {sharing ? 'Generando...' : 'WhatsApp'}
          </Button>
          <Button
            variant="secondary"
            onClick={handleConectarBluetooth}
            disabled={bluetoothConectando}
            className="flex items-center justify-center gap-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-800 border-indigo-300"
          >
            <Bluetooth size={16} />
            {bluetoothConectando ? 'Conectando...' : 'Bluetooth'}
          </Button>
          <Button
            variant="primary"
            onClick={handlePrint}
            disabled={printing}
            className="bg-green-600 hover:bg-green-500 flex items-center justify-center gap-2"
          >
            <Printer size={16} />
            {printing ? 'Imprimiendo...' : (printed ? 'Reimprimir' : 'Imprimir')}
          </Button>
        </div>

        <div className="w-full max-w-2xl">
          <Button
            variant="secondary"
            onClick={handlePrintBluetooth}
            disabled={bluetoothImprimiendo}
            className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white border-indigo-500"
          >
            <Bluetooth size={16} />
            {bluetoothImprimiendo ? 'Enviando a impresora Bluetooth...' : 'Imprimir por Bluetooth (ESC/POS)'}
          </Button>
        </div>

        <p className="text-xs text-gray-400">
          Enter o * para imprimir · ESC para cerrar
        </p>
      </div>
    </Modal>
  );
};

export default TicketPreviewModal;
